/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import Code from '@atlaskit/code/code';
import { cssMap, jsx } from '@atlaskit/css';
import { Text } from '@atlaskit/primitives/compiled';
import { Table, TBody, TD, TH, THead, TR } from '@atlaskit/table/primitives';
import { token } from '@atlaskit/tokens';

import HorizontalScrollContainer from '../horizontal-scroll-container';
import { values } from './values';

/**
 * __Radius table__
 */
export const RadiusTable = (): React.JSX.Element => {
	return (
		<HorizontalScrollContainer>
			<Table testId="radius-table">
				<THead>
					<TR isBodyRow={false}>
						<TH width="110px">Token name</TH>
						<TH width="430px">Suitable for</TH>
						<TH width="90px">Value*</TH>
						<TH width="70px">Visual preview</TH>
					</TR>
				</THead>
				<TBody>
					{values.map((token) => {
						return (
							<TR key={token.tokenName}>
								<TD>
									<Code>{token.tokenName}</Code>
								</TD>
								<TD>
									<Text>{token.suitableFor}</Text>
								</TD>
								<TD>
									<Text>{token.value}</Text>
								</TD>
								<TD>
									{token.tokenName !== 'radius.tile' && (
										<RadiusPreview size={token.tokenName.replace('radius.', '') as RadiusSize} />
									)}
								</TD>
							</TR>
						);
					})}
				</TBody>
			</Table>
		</HorizontalScrollContainer>
	);
};

type RadiusSize = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'full';

const styles = cssMap({
	base: {
		backgroundColor: token('color.background.neutral'),
		width: '45px',
		height: '45px',
		position: 'relative',
		'&::after': {
			content: '',
			position: 'absolute',
			// @ts-expect-error - need to position the element outside the original
			insetBlockEnd: '-1px',
			// @ts-expect-error - need to position the element outside the original
			insetInlineStart: '-1px',
			width: '50%',
			height: '50%',
			borderInlineStartWidth: token('border.width.focused'),
			borderBlockEndWidth: token('border.width.focused'),
			borderBlockStartWidth: '0',
			borderInlineEndWidth: '0',
			borderColor: token('color.border.accent.magenta'),
			borderStyle: 'solid',
		},
	},
	xsmall: {
		borderRadius: token('radius.xsmall'),
		'&::after': {
			borderEndStartRadius: token('radius.xsmall'),
		},
	},
	small: {
		borderRadius: token('radius.small'),
		'&::after': {
			borderEndStartRadius: token('radius.small'),
		},
	},
	medium: {
		borderRadius: token('radius.medium'),
		'&::after': {
			borderEndStartRadius: token('radius.medium'),
		},
	},
	large: {
		borderRadius: token('radius.large'),
		'&::after': {
			borderEndStartRadius: token('radius.large'),
		},
	},
	xlarge: {
		borderRadius: token('radius.xlarge'),
		'&::after': {
			borderEndStartRadius: token('radius.xlarge'),
		},
	},
	xxlarge: {
		borderRadius: token('radius.xxlarge'),
		'&::after': {
			borderEndStartRadius: token('radius.xxlarge'),
		},
	},
	full: {
		borderRadius: token('radius.full'),
		'&::after': {
			borderEndStartRadius: token('radius.full'),
		},
	},
});

const RadiusPreview = ({ size }: { size: RadiusSize }) => {
	return <div css={[styles.base, styles[size as RadiusSize]]}></div>;
};
